import { useMsal } from "@azure/msal-react";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getToken, useMsalAuth } from "../authConfig";
import { getOrganizationContext, selectOrganization } from "../api/organizationAccessApi";
import { getCurrentUser } from "../api/currentUser";
import { LoginContext } from "../loginContext";
import { AccessRole, OrganizationAccessContext, OrganizationContextMembership } from "../pages/organization-admin/types";
import { canAccessOrganizationAdministration, isOrganizationAdminMembership, requiresOrganizationAdminForAdministration } from "./organizationContextSelectors";

interface OrganizationContextValue {
    context: OrganizationAccessContext | null;
    currentMembership: OrganizationContextMembership | null;
    loading: boolean;
    switching: boolean;
    error: string | null;
    canAdminister: boolean;
    canAccessAdministration: boolean;
    administrationRequiresOrgAdmin: boolean;
    isCompanyAdmin: boolean;
    isOrganizationAdmin: boolean;
    refresh: () => Promise<void>;
    switchOrganization: (organizationId: string) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

const administrativeRoles: ReadonlySet<AccessRole> = new Set(["Organization Admin", "Business Area Admin", "Unit Manager"]);

export const OrganizationProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const { loggedIn } = useContext(LoginContext);
    const { instance } = useMsal();
    const client = useMsalAuth ? instance : undefined;
    const [context, setContext] = useState<OrganizationAccessContext | null>(null);
    const [loading, setLoading] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Company Profile's role is the source of truth for administration rights.
    const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);

    const getIdToken = useCallback(async (): Promise<string | undefined> => (client ? await getToken(client) : undefined), [client]);

    const refresh = useCallback(async (): Promise<void> => {
        if (!loggedIn) {
            setContext(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setContext(await getOrganizationContext(await getIdToken()));
        } catch (caughtError: unknown) {
            setContext(null);
            setError(caughtError instanceof Error ? caughtError.message : "Failed to load Organization context");
        } finally {
            setLoading(false);
        }
    }, [getIdToken, loggedIn]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    useEffect(() => {
        let cancelled = false;

        const loadCompanyRole = async () => {
            if (!loggedIn) {
                setIsCompanyAdmin(false);
                return;
            }
            try {
                const result = await getCurrentUser({ idToken: await getIdToken() });
                if (!cancelled) {
                    setIsCompanyAdmin(String(result.user?.company_role ?? "").toLowerCase() === "admin");
                }
            } catch {
                if (!cancelled) {
                    setIsCompanyAdmin(false);
                }
            }
        };

        void loadCompanyRole();

        return () => {
            cancelled = true;
        };
    }, [getIdToken, loggedIn]);

    const currentMembership = useMemo(
        () => context?.memberships.find(membership => membership.organization.id === context.currentOrganizationId) ?? null,
        [context]
    );
    const isOrganizationAdmin = useMemo(() => isOrganizationAdminMembership(currentMembership), [currentMembership]);
    // A Company Profile admin can always administer. An explicit administrative
    // role in the Organization access model still grants it too, so users who are
    // org admins without being company admins keep their access.
    const canAdminister = useMemo(
        () => isCompanyAdmin || (currentMembership?.explicitRoleAssignments.some(assignment => administrativeRoles.has(assignment.role)) ?? false),
        [currentMembership, isCompanyAdmin]
    );

    const administrationRequiresOrgAdmin = requiresOrganizationAdminForAdministration(currentMembership?.organization.id);
    const canAccessAdministration = canAccessOrganizationAdministration(currentMembership, canAdminister, administrationRequiresOrgAdmin);

    const switchOrganization = useCallback(
        async (organizationId: string): Promise<void> => {
            setSwitching(true);
            setError(null);
            setContext(null);
            try {
                await selectOrganization(organizationId, await getIdToken());
                window.location.reload();
            } catch (caughtError: unknown) {
                setError(caughtError instanceof Error ? caughtError.message : "Failed to switch Organization");
                await refresh();
                setSwitching(false);
            }
        },
        [getIdToken, refresh]
    );

    return (
        <OrganizationContext.Provider
            value={{
                context,
                currentMembership,
                loading,
                switching,
                error,
                canAdminister,
                canAccessAdministration,
                administrationRequiresOrgAdmin,
                isCompanyAdmin,
                isOrganizationAdmin,
                refresh,
                switchOrganization
            }}
        >
            {children}
        </OrganizationContext.Provider>
    );
};

export const useOrganization = (): OrganizationContextValue => {
    const context = useContext(OrganizationContext);
    if (context === undefined) {
        throw new Error("useOrganization must be used within an OrganizationProvider");
    }
    return context;
};
